import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UploadCloud, Loader2, Trash2, Sparkles, Palette } from "lucide-react";

export default function VisionVaultPage({ onUsePalette }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const fileRef = useRef(null);

  const load = () => api.getInspirations().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const res = await api.addInspiration(f, note);
      setItems((x) => [res, ...x]);
      setNote("");
      toast.success("Inspiration analysed", { description: res.mood || "Added to the vault" });
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || "Try another image." });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    await api.deleteInspiration(id);
    setItems((x) => x.filter((i) => i.id !== id));
    toast.success("Removed from vault");
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 min-h-screen">
      <header className="pb-6 border-b border-white/10">
        <div className="overline-label">Atelier Vision Vault</div>
        <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight mt-1">Inspiration & Moodboard</h2>
        <p className="text-neutral-400 mt-2 max-w-2xl">
          Drop sketches, fabrics, architecture or editorial references. ATELIER reads each one like a
          designer — silhouette, palette, mood and a direction to push it further.
        </p>
      </header>

      <div className="card-lux rounded-xl p-6 sm:p-8 mt-8">
        <input
          data-testid="vault-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note — what drew you to this? (e.g. 'the way the fabric folds')"
          className="w-full bg-[#141418] border border-white/10 focus:border-amber-500/50 rounded-lg px-4 py-3 text-neutral-100 placeholder:text-neutral-600 outline-none mb-4"
        />
        <label
          data-testid="inspiration-upload-label"
          className="flex flex-col items-center justify-center gap-3 border border-dashed border-white/15 hover:border-amber-500/50 rounded-xl py-12 cursor-pointer transition-colors"
        >
          {uploading ? (
            <>
              <Loader2 className="animate-spin text-amber-400" size={28} />
              <span className="text-neutral-300">Reading your reference…</span>
            </>
          ) : (
            <>
              <UploadCloud className="text-amber-400" size={30} />
              <span className="text-neutral-200 font-medium">Upload an inspiration image</span>
              <span className="text-xs text-neutral-500 font-mono">JPG · PNG · WEBP</span>
            </>
          )}
          <input ref={fileRef} data-testid="inspiration-upload-input" type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
        </label>
      </div>

      {items.length === 0 && !uploading && (
        <p className="text-neutral-500 text-center mt-16">Your vision vault is empty — upload the first spark.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-8">
        {items.map((it) => (
          <div key={it.id} className="card-lux rounded-xl overflow-hidden flex flex-col" data-testid={`inspiration-card-${it.id}`}>
            <div className="relative group">
              <img src={`data:image/jpeg;base64,${it.image_base64}`} alt="inspiration" className="w-full h-60 object-cover" />
              <button
                onClick={() => remove(it.id)}
                data-testid={`delete-inspiration-${it.id}`}
                className="absolute top-3 right-3 p-2 rounded-full bg-black/60 text-neutral-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 size={15} />
              </button>
            </div>
            <div className="p-5 flex flex-col gap-3">
              {it.mood && (
                <div className="flex items-center gap-2">
                  <Sparkles size={13} className="text-amber-400" />
                  <span className="font-serif text-xl text-neutral-100">{it.mood}</span>
                </div>
              )}
              <p className="text-sm text-neutral-400 leading-relaxed">{it.analysis}</p>

              {it.palette?.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  {it.palette.map((c, i) => (
                    <div key={i} className="flex flex-col items-center">
                      <span className="w-9 h-9 rounded-md border border-white/10" style={{ background: c }} title={c} />
                      <span className="text-[9px] font-mono text-neutral-500 mt-1">{c}</span>
                    </div>
                  ))}
                </div>
              )}

              {it.tags?.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  {it.tags.map((t, i) => (
                    <span key={i} className="text-[11px] font-mono px-2 py-1 rounded-full border border-white/10 text-neutral-400">
                      {t}
                    </span>
                  ))}
                </div>
              )}

              {it.direction && (
                <p className="text-sm text-amber-200/80 border-l-2 border-amber-500/40 pl-3 italic">{it.direction}</p>
              )}

              <button
                data-testid={`use-palette-${it.id}`}
                onClick={() =>
                  onUsePalette?.({
                    palette: it.palette || [],
                    mood: it.mood || "",
                    tags: it.tags || [],
                    direction: it.direction || "",
                  })
                }
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-lg border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors px-4 py-2.5 text-sm font-medium"
              >
                <Palette size={15} /> Use palette in Runway
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
